import { useState, useEffect } from 'react';

export interface ReleaseNote {
  version: string;
  releaseDate: string;
  title: string;
  changes: {
    features?: Array<{
      title: string;
      description: string;
    }>;
    improvements?: Array<{
      title: string;
      description: string;
    }>;
    bugFixes?: Array<{
      title: string;
      description: string;
    }>;
  };
}

const RELEASE_NOTES_KEY = 'lastViewedReleaseVersion';

export const useReleaseNotes = () => {
  const [releaseNotes, setReleaseNotes] = useState<ReleaseNote[]>([]);
  const [hasNewReleases, setHasNewReleases] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Load release notes data
  useEffect(() => {
    const loadReleaseNotes = async () => {
      try {
        // Import the JSON data
        const response = await import('../data/release-notes.json');
        const notes: ReleaseNote[] = response.default || response;
        
        // Sort by release date (newest first)
        const sortedNotes = notes.sort((a, b) => 
          new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime()
        );
        
        setReleaseNotes(sortedNotes);
        
        // Check if there are new releases
        checkForNewReleases(sortedNotes);
      } catch (error) {
        console.error('Error loading release notes:', error);
        setReleaseNotes([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadReleaseNotes();
  }, []);

  // Check if user has new releases to view
  const checkForNewReleases = (notes: ReleaseNote[]) => {
    if (notes.length === 0) return;

    const lastViewedVersion = localStorage.getItem(RELEASE_NOTES_KEY);
    const latestVersion = notes[0]?.version; // First item is the newest

    // If no previous version stored or latest version is different, show badge
    if (!lastViewedVersion || lastViewedVersion !== latestVersion) {
      setHasNewReleases(true);
    } else {
      setHasNewReleases(false);
    }
  };

  // Mark a specific version as viewed
  const markVersionAsViewed = (version: string) => {
    localStorage.setItem(RELEASE_NOTES_KEY, version);
    
    // Check if this was the latest version
    const latestVersion = releaseNotes[0]?.version;
    if (version === latestVersion) {
      setHasNewReleases(false);
    }
  };

  // Mark latest version as viewed (when user opens the modal)
  const markLatestAsViewed = () => {
    if (releaseNotes.length > 0) {
      markVersionAsViewed(releaseNotes[0].version);
    }
  };

  // Reset viewed status (for testing purposes)
  const resetViewedStatus = () => {
    localStorage.removeItem(RELEASE_NOTES_KEY);
    setHasNewReleases(true);
  };

  return {
    releaseNotes,
    hasNewReleases,
    isLoading,
    markVersionAsViewed,
    markLatestAsViewed,
    resetViewedStatus,
  };
};
